import { useCallback, useEffect, useMemo, useRef } from 'react';

import { diffDocuments, settledGenerations } from './document-sync';
import { useVariantSetPersistence } from './persistence-adapter';
import { useMediaUrls } from './useMediaUrls';

import { useBriefConcept } from '@/hooks/useBriefConcept';
import type { ConceptStatus } from '@/lib/briefs/concept-contract';
import { evaluateReadyGate } from '@/lib/concept-workbench/ready-gate';
import { toDisplayStatus } from '@/lib/concept-workbench/status-vocabulary';
import type { SetLayer, VariantSetDocument, VariantState } from '@/lib/concept-workbench/types';
import { useVariantSetStore } from '@/lib/concept-workbench/useVariantSetStore';

/**
 * The workbench's one data entry point.
 *
 * It stitches three things that must not know about each other:
 *
 *   1. the CONCEPT — loaded and saved through `useBriefConcept`, i.e. the exact
 *      RPC + optimistic-locking contract `BriefConceptPage` already uses. The
 *      brief's save model is explicit ⌘S with a version CAS and is deliberately
 *      NOT unified with the canvas's 800ms autosave: the brief is a shared,
 *      reviewed, versioned document, the canvas is private and high-frequency.
 *      Merging them would either make the brief lossy or the canvas unusable.
 *   2. the VARIANT SET — the in-memory store, which is the thing the surface
 *      actually edits.
 *   3. PERSISTENCE — behind the adapter seam, so the store never learns that a
 *      database exists.
 *
 * The store is hydrated from persistence on load and on membership changes, and
 * leads the rest of the time. Re-hydrating on every query settle would replace
 * the document the user is mid-drag with the server's copy of it — the same trap
 * the autosave layer calls out about invalidation.
 */
export function useConceptWorkbench(briefId?: string, projectTaskId?: string) {
  const conceptQuery = useBriefConcept(briefId, projectTaskId);
  const concept = conceptQuery.data ?? null;

  const platforms = useMemo(
    () => concept?.intents.map((intent) => intent.platform) ?? [],
    [concept],
  );

  const persistence = useVariantSetPersistence(projectTaskId, platforms);

  const document = useVariantSetStore((state) => state.document);
  const selectedVariantId = useVariantSetStore((state) => state.selectedVariantId);
  const selectedLayerIds = useVariantSetStore((state) => state.selectedLayerIds);
  const loadSet = useVariantSetStore((state) => state.loadSet);
  const clear = useVariantSetStore((state) => state.clear);
  const past = useVariantSetStore((state) => state.past);
  const future = useVariantSetStore((state) => state.future);
  const setVariantImage = useVariantSetStore((state) => state.setVariantImage);
  const setGenerating = useVariantSetStore((state) => state.setGenerating);
  const directPatches = useRef<
    Map<string, { mediaId?: string; followsSetImage?: boolean; isGenerating?: boolean }>
  >(new Map());

  /**
   * Hydrate on concept change, and again whenever MEMBERSHIP moves.
   *
   * "Once per concept" was the first rule and it was wrong on the live route:
   * `ensureRenditions` inserts the rows and updates the cache, but the store had
   * already been hydrated from the empty document, so the surface kept showing
   * "No sizes yet" while the rows existed in the database. A silent no-op of
   * exactly the class this repo keeps shipping — invisible to tsc, to lint and to
   * every unit test, and visible in one click on the real page.
   *
   * The guard is the VARIANT ID SET, not the document's object identity. Identity
   * churns on every autosave version write, so keying on it would replace the
   * document the user is dragging every 800ms. Membership, by contrast, only ever
   * changes through a server round-trip (`ensureRenditions` / `addRatio` /
   * `removeRatio`), which is precisely when a re-hydrate is correct.
   */
  const hydratedKey = useRef<string | null>(null);
  useEffect(() => {
    const loaded = persistence.document;
    if (!loaded || !projectTaskId) return;
    const key = `${projectTaskId}:${loaded.variants
      .map((variant) => variant.id)
      .sort()
      .join(',')}`;
    if (hydratedKey.current === key) return;
    hydratedKey.current = key;
    loadSet(loaded);
  }, [persistence.document, projectTaskId, loadSet]);

  useEffect(() => {
    return () => {
      hydratedKey.current = null;
      clear();
    };
  }, [clear]);

  // Store → persistence. The previous document is held in a ref rather than
  // derived from the query cache: the cache is the SERVER's copy and lags by one
  // debounce window, so diffing against it would re-send the same patch every
  // 800ms until the flush landed.
  const lastSynced = useRef<VariantSetDocument | null>(null);
  useEffect(() => {
    if (!document) {
      lastSynced.current = null;
      return;
    }
    if (lastSynced.current === null) {
      lastSynced.current = document;
      return;
    }
    const patches = diffDocuments(lastSynced.current, document);
    lastSynced.current = document;
    if (patches.set) persistence.saveSet(patches.set);
    for (const { id, patch } of patches.variants) {
      const direct = directPatches.current.get(id);
      if (direct) {
        const remaining = { ...patch };
        if (direct.mediaId === remaining.mediaId) delete remaining.mediaId;
        if (direct.followsSetImage === remaining.followsSetImage) delete remaining.followsSetImage;
        if (direct.isGenerating === remaining.isGenerating) delete remaining.isGenerating;
        directPatches.current.delete(id);
        if (Object.keys(remaining).length > 0) persistence.saveVariant(id, remaining);
      } else {
        persistence.saveVariant(id, patch);
      }
    }
  }, [document, persistence]);

  const markDirectPatch = useCallback(
    (
      renditionId: string,
      patch: { mediaId?: string; followsSetImage?: boolean; isGenerating?: boolean },
    ) => {
      directPatches.current.set(renditionId, {
        ...directPatches.current.get(renditionId),
        ...patch,
      });
    },
    [],
  );

  /**
   * Persisted → store, for ONE case only: a generation that finished WITHOUT
   * this tab. The server attaches the media itself
   * (`attachGeneratedMediaByCorrelation`) and the rendition query polls while
   * anything is generating, so after a reload/HMR the persisted row goes
   * `is_generating:false` + `media_id` while the store — hydrated once, by
   * membership — still shows "Generating…". Sync exactly those variants, and
   * mark them direct so the store→persistence diff does not write the same
   * values straight back. Everything else stays store-led (the mid-drag rule).
   */
  useEffect(() => {
    for (const { id, mediaId } of settledGenerations(persistence.document, document)) {
      if (mediaId) {
        markDirectPatch(id, { mediaId, followsSetImage: false, isGenerating: false });
        setVariantImage(id, mediaId);
      } else {
        markDirectPatch(id, { isGenerating: false });
      }
      setGenerating(id, false);
    }
  }, [persistence.document, document, markDirectPatch, setVariantImage, setGenerating]);

  const renditionLifecycle = useMemo(
    () => ({
      setGenerating: async (
        renditionId: string,
        isGenerating: boolean,
        generationToken?: string,
      ) => {
        await persistence.setGenerating(renditionId, isGenerating, generationToken);
        markDirectPatch(renditionId, { isGenerating });
        setGenerating(renditionId, isGenerating);
      },
      attachMedia: async (renditionId: string, mediaId: string) => {
        await persistence.attachMedia(renditionId, mediaId);
        markDirectPatch(renditionId, { mediaId, followsSetImage: false });
        setVariantImage(renditionId, mediaId);
      },
    }),
    [persistence, markDirectPatch, setGenerating, setVariantImage],
  );

  const selectedVariant: VariantState | null = useMemo(
    () => document?.variants.find((variant) => variant.id === selectedVariantId) ?? null,
    [document, selectedVariantId],
  );

  const selectedLayer: SetLayer | null = useMemo(() => {
    if (selectedLayerIds.length !== 1) return null;
    return document?.layers.find((layer) => layer.id === selectedLayerIds[0]) ?? null;
  }, [document, selectedLayerIds]);

  const mediaIds = useMemo(
    () => [document?.mediaId ?? null, ...(document?.variants.map((v) => v.mediaId) ?? [])],
    [document],
  );
  const {
    urls: mediaUrls,
    cardRenderers: mediaCardRenderers,
    isResolving: isResolvingImage,
  } = useMediaUrls(mediaIds);

  const readyGate = useMemo(() => evaluateReadyGate(document), [document]);

  const displayStatus = useMemo(
    () => (concept ? toDisplayStatus(concept.status as ConceptStatus) : 'idea'),
    [concept],
  );

  return {
    concept,
    conceptQuery,
    persistence,

    document,
    selectedVariant,
    selectedVariantId,
    selectedLayer,
    selectedLayerIds,

    mediaUrls,
    mediaCardRenderers,
    selectedImageUrl: selectedVariant?.mediaId
      ? (mediaUrls[selectedVariant.mediaId] ?? null)
      : null,
    /**
     * The selected variant HAS an image whose URL has not arrived yet.
     *
     * Distinct from `selectedImageUrl === null`, which the empty state reads.
     * Verified on the live route: after a reload the canvas showed "No image for
     * this size yet" — above a Generate button that spends image quota — for the
     * ~1s the `media` lookup was in flight, on a rendition that already had one.
     */
    isResolvingImage: Boolean(selectedVariant?.mediaId) && isResolvingImage,

    readyGate,
    displayStatus,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    renditionLifecycle,
  };
}
